import { useMutation, useQueryClient } from '@tanstack/react-query'
import AuthContext from '../../context/AuthContext'
import { useContext, useState } from 'react'
import { getAuth } from 'firebase/auth'
import ApiContext from '../../context/ApiContext'
import { toast } from 'sonner'
import { Company, getCompaniesQueryKey } from '../queries/useCompanies'

const useCreateCompany = () => {
	const auth = getAuth()
	const authContext = useContext(AuthContext)
	const apiContext = useContext(ApiContext)
	const queryClient = useQueryClient()
	const [isCreateOpen, setIsCreateOpen] = useState(false)
	const [newCompanyName, setNewCompanyName] = useState('')

	const createCompany = async (): Promise<Company> => {
		const idToken = await auth.currentUser?.getIdToken(true)
		try {
			const response = await fetch(`${apiContext.apiUrl}/companies`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${idToken}`,
				},
				body: JSON.stringify({
					name: newCompanyName,
				}),
			})
			if (!response.ok) throw new Error(await response.text())
			return response.json()
		} catch (error) {
			throw error
		}
	}

	const { mutate: handleCreateCompany, isPending: loading } = useMutation({
		mutationFn: createCompany,
		onSuccess: async newCompany => {
			await queryClient.invalidateQueries({ queryKey: getCompaniesQueryKey() })
			authContext.setCompany(newCompany)
			toggleCreateCompanyDialog()
		},
		onError: (error: unknown) => {
			if (error instanceof Error) {
				toast.error('Something went wrong', {
					description: error.message,
				})
			}
		},
	})

	const toggleCreateCompanyDialog = () => {
		setIsCreateOpen(prevValue => {
			const newValue = !prevValue
			if (newValue) {
				setNewCompanyName('')
			}
			return newValue
		})
	}

	return { isCreateOpen, toggleCreateCompanyDialog, setNewCompanyName, newCompanyName, handleCreateCompany, loading }
}

export default useCreateCompany
